import { z } from 'zod';
import { IsoDateTime, Uuid, patchOf } from './common';
import { Material } from './materials';

const Centavos = z.number().int().min(0).max(100_000_000);
const Watts = z.number().int().min(0).max(5000);
const Text = (max = 200) => z.string().trim().max(max).nullable();

export const PrinterStatus = z.enum(['idle', 'printing', 'maintenance', 'out_of_service']);
export type PrinterStatus = z.infer<typeof PrinterStatus>;
export const PRINTER_STATUS_LABELS: Record<PrinterStatus, string> = {
  idle: 'Livre',
  printing: 'Imprimindo',
  maintenance: 'Manutenção',
  out_of_service: 'Fora de serviço',
};

const PrinterFields = z.object({
  name: z.string().trim().min(1).max(80).describe('Apelido, e.g. "X2D".'),
  model: z.string().trim().min(1).max(120),
  purchasePriceCentavos: Centavos,
  purchaseDate: z.iso.date().nullable().default(null),
  lifetimeHours: z
    .number()
    .int()
    .min(1)
    .max(200_000)
    .describe('Expected lifetime in print hours (5.000 h starting guess).'),
  powerLowWatts: Watts.describe('Average draw printing PLA/PETG/TPU.'),
  powerHighWatts: Watts.describe('Average draw printing ABS/ASA/PC.'),
  maintenancePerHourCentavos: Centavos.default(10).describe(
    'Estimated maintenance cost per print hour.',
  ),
  buildVolume: Text(80).default(null),
  materials: z.array(Material).max(20).default([]),
  nozzles: Text().default(null),
  multicolorUnit: Text(80).default(null),
  multicolorSlots: z.number().int().min(0).max(64).nullable().default(null),
  enclosure: Text(120).default(null),
  externalRef: Text().default(null),
});

export const CreatePrinterBody = PrinterFields.meta({
  example: {
    name: 'X2D',
    model: 'Bambu Lab X2D',
    purchasePriceCentavos: 1_249_900,
    lifetimeHours: 5000,
    powerLowWatts: 250,
    powerHighWatts: 550,
    maintenancePerHourCentavos: 10,
  },
});
export type CreatePrinterBody = z.input<typeof CreatePrinterBody>;
export const UpdatePrinterBody = patchOf(PrinterFields);
export type UpdatePrinterBody = z.input<typeof UpdatePrinterBody>;

export const SetPrinterStatusBody = z.object({ status: PrinterStatus });

const HourCost = z.object({
  depreciation: z.number().int(),
  energy: z.number().int().nullable().describe('Null while the energy tariff is not set.'),
  maintenance: z.number().int(),
  total: z.number().int(),
});

export const Printer = z
  .object({
    id: Uuid,
    name: z.string(),
    model: z.string(),
    purchasePriceCentavos: z.number().int(),
    purchaseDate: z.string().nullable(),
    lifetimeHours: z.number().int(),
    powerLowWatts: z.number().int(),
    powerHighWatts: z.number().int(),
    maintenancePerHourCentavos: z.number().int(),
    buildVolume: z.string().nullable(),
    materials: z.array(Material),
    nozzles: z.string().nullable(),
    multicolorUnit: z.string().nullable(),
    multicolorSlots: z.number().int().nullable(),
    enclosure: z.string().nullable(),
    status: PrinterStatus,
    hourMeterHours: z
      .number()
      .describe('Starting hours + real hours of finished and failed jobs ("horímetro").'),
    retiredAt: IsoDateTime.nullable(),
    countsForLifetime: z.boolean(),
    externalRef: z.string().nullable(),
    costPerHour: z
      .object({ low: HourCost, high: HourCost })
      .describe(
        'Machine cost per print hour in centavos, by power group, with the current shop tariff + bandeira.',
      ),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Printer' });
export type Printer = z.infer<typeof Printer>;

export const PrinterList = z.object({ data: z.array(Printer), nextCursor: z.string().nullable() });

export const PrinterModel = z.object({
  id: z.string(),
  name: z.string(),
  complete: z.boolean().describe('Full technical data available; otherwise check the spec sheet.'),
  defaults: PrinterFields.omit({ name: true, externalRef: true }).partial(),
});
export type PrinterModel = z.infer<typeof PrinterModel>;
export const PrinterModelList = z.object({ data: z.array(PrinterModel) });

/** Known models for the wizard. Only the X2D has complete data (spec "Printer default"). */
export const PRINTER_MODELS: PrinterModel[] = [
  {
    id: 'bambu-x2d',
    name: 'Bambu Lab X2D',
    complete: true,
    defaults: {
      model: 'Bambu Lab X2D',
      purchasePriceCentavos: 1_249_900,
      lifetimeHours: 5000,
      powerLowWatts: 250,
      powerHighWatts: 550,
      maintenancePerHourCentavos: 10,
      buildVolume: '256 × 256 × 260 mm',
      materials: ['PLA', 'PLA Silk', 'PLA Matte', 'PETG', 'TPU', 'ABS', 'ASA', 'PC'],
      nozzles: '2 · principal 0,4 mm + auxiliar para suporte',
      multicolorUnit: 'AMS 2 Pro',
      multicolorSlots: 4,
      enclosure: 'Aquecida, até 65 °C',
    },
  },
  ...[
    'Bambu Lab X1 Carbon',
    'Bambu Lab P1S',
    'Bambu Lab A1',
    'Bambu Lab A1 mini',
    'Prusa MK4S',
    'Prusa CORE One',
    'Creality K1C',
    'Creality Ender-3 V3',
  ].map((name) => ({
    id: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    name,
    complete: false,
    defaults: { model: name, lifetimeHours: 5000, maintenancePerHourCentavos: 10 },
  })),
];

export const MaintenanceState = z.enum(['ok', 'soon', 'due']);
export const MaintenanceTask = z.object({
  id: Uuid,
  name: z.string(),
  intervalHours: z.number().int(),
  lastDoneHours: z.number().nullable(),
  nextAtHours: z.number(),
  dueInHours: z.number().describe('Negative = overdue by that many hours.'),
  state: MaintenanceState.describe('`soon` within 50 h, `due` at or past the interval.'),
});
export type MaintenanceTask = z.infer<typeof MaintenanceTask>;
export const CreateMaintenanceTaskBody = z.object({
  name: z.string().trim().min(1).max(120),
  intervalHours: z.number().int().min(1).max(100_000),
});
export const UpdateMaintenanceTaskBody = CreateMaintenanceTaskBody.partial();

export const MaintenanceLogEntry = z.object({
  id: Uuid,
  taskId: Uuid.nullable(),
  description: z.string(),
  doneOn: z.iso.date(),
  parts: z.string().nullable(),
  costCentavos: z.number().int(),
  hourMeterHours: z.number(),
  actor: z.string(),
});
export const CreateMaintenanceLogBody = z
  .object({
    taskId: Uuid.nullable().default(null).describe('Marks that plan task as done now.'),
    description: z.string().trim().max(200).nullable().default(null),
    doneOn: z.iso.date(),
    parts: z.string().trim().max(300).nullable().default(null),
    costCentavos: z.number().int().min(0).max(100_000_000).default(0),
  })
  .refine((b) => b.taskId !== null || (b.description ?? '').length > 0, {
    message: 'Escolha a tarefa ou descreva a manutenção.',
    path: ['description'],
  });

export const PrinterOverview = z
  .object({
    hourMeterHours: z.number(),
    hours30d: z.number(),
    utilization30dPct: z.number().describe('Print hours in the last 30 days ÷ 720 h.'),
    jobsDone: z.number().int(),
    jobsFailed: z.number().int(),
    failureRatePct: z.number().nullable(),
    hoursByMaterial: z.array(z.object({ material: z.string(), hours: z.number() })),
    maintenanceCostCentavos: z.number().int().describe('Sum of the maintenance log.'),
    suggestedMaintenancePerHourCentavos: z
      .number()
      .int()
      .nullable()
      .describe('Logged parts ÷ print hours ("Usar este valor"); null until both exist.'),
    tasks: z.array(MaintenanceTask),
    log: z.array(MaintenanceLogEntry),
    alerts: z.number().int().describe('Tasks due or due soon.'),
    next: z
      .object({ number: z.string(), label: z.string(), estSeconds: z.number().int() })
      .nullable()
      .describe('Next queued job.'),
  })
  .meta({ id: 'PrinterOverview' });
export type PrinterOverview = z.infer<typeof PrinterOverview>;

export const RetirePrinterBody = z.object({
  countsForLifetime: z
    .boolean()
    .default(true)
    .describe('Use its final hours in the default lifetime of new printers.'),
});
export const LifetimeDefault = z.object({
  hours: z.number().int(),
  retiredCount: z.number().int().describe('Retired printers whose hours count.'),
});
